import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Doors & locations: rename the doors FaciliKey created and the location each
 * one belongs to. Events keep the FaciliKey door id, so a rename changes how
 * every past and future event displays.
 */
const DoorsPanel = ({ api, doors: loaded, open, onClose, onSaved }) => {
  const { t } = useTranslation("coaching-portal");
  const [original, setOriginal] = useState([]);
  const [edits, setEdits] = useState({});
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    if (!open) return;
    setOriginal(loaded || []);
    setEdits({});
  }, [open, loaded]);

  if (!open) return null;

  const loadError = !loaded;
  const close = () => {
    setStatus("idle");
    onClose();
  };

  const typed = (door, field) => edits[door.id]?.[field] ?? door[field] ?? "";
  const edit = (id, field, value) =>
    setEdits((current) => ({
      ...current,
      [id]: { ...current[id], [field]: value },
    }));
  const changed = (door) =>
    ["displayName", "locationName"].some((field) => {
      const value = typed(door, field).trim();
      if (field === "displayName" && value === "") return false;
      return value !== (door[field] ?? "");
    });
  const dirty = original.some(changed);

  const save = async () => {
    setStatus("saving");
    try {
      const res = await api("gym-doors", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          doors: original.map((door) => ({
            id: door.id,
            displayName: typed(door, "displayName").trim() || door.displayName,
            locationName: typed(door, "locationName").trim(),
          })),
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      setOriginal(data.doors);
      setEdits({});
      setStatus("saved");
      onSaved();
    } catch {
      setStatus("saveError");
    }
  };

  return (
    <>
      <div className="door-scrim" onClick={close} aria-hidden="true" />
      <aside //NOSONAR
        className="door-drawer"
        role="dialog"
        aria-label={t("doorAccess.doors.title")}
      >
        <header className="door-drawer__header">
          <h2>{t("doorAccess.doors.title")}</h2>
          <button
            type="button"
            className="door-icon-button"
            aria-label={t("doorAccess.drawer.close")}
            onClick={close}
          >
            <img src="/img/new-icons/close-modal.svg" alt="" />
          </button>
        </header>
        <p className="door-muted">{t("doorAccess.doors.intro")}</p>

        {loadError && (
          <p className="door-form-error">{t("doorAccess.doors.loadError")}</p>
        )}
        {!loadError && original.length === 0 && (
          <p className="door-muted">{t("doorAccess.doors.empty")}</p>
        )}

        <div className="door-doors-list">
          {original.map((door) => (
            <fieldset key={door.id} className="door-doors-row">
              <legend>
                {t("doorAccess.doors.faciliKeyId")}: {door.facilikeyDoorId}
              </legend>
              <label>
                {t("doorAccess.doors.doorName")}
                <input
                  type="text"
                  maxLength={80}
                  value={typed(door, "displayName")}
                  onChange={(event) =>
                    edit(door.id, "displayName", event.target.value)
                  }
                />
              </label>
              <label>
                {t("doorAccess.doors.location")}
                <input
                  type="text"
                  maxLength={80}
                  value={typed(door, "locationName")}
                  onChange={(event) =>
                    edit(door.id, "locationName", event.target.value)
                  }
                />
              </label>
            </fieldset>
          ))}
        </div>

        {status === "saveError" && (
          <p className="door-form-error">{t("doorAccess.doors.error")}</p>
        )}
        {status === "saved" && !dirty && (
          <p className="door-form-ok">{t("doorAccess.doors.saved")}</p>
        )}

        <footer className="door-drawer__footer">
          <button
            type="button"
            data-button-type="secondary"
            disabled={!dirty || status === "saving"}
            onClick={() => setEdits({})}
          >
            {t("doorAccess.doors.discard")}
          </button>
          <button
            type="button"
            data-button-type="primary"
            disabled={!dirty || status === "saving"}
            onClick={save}
          >
            {status === "saving"
              ? t("doorAccess.doors.saving")
              : t("doorAccess.doors.save")}
          </button>
        </footer>
      </aside>
    </>
  );
};

export default DoorsPanel;
